import { useCallback, useEffect, useState } from 'react'
import {
  CustomFieldDataType,
  CustomListItemStyle,
  alphabeticalSort,
} from 'shared'
import { useProjectCustomFieldsContext } from '../ProjectCustomFieldsContext'
import CustomFieldRow from './CustomFieldRow'
import { CustomField, CustomFieldEditorProps } from './types'
import ReportRecordSelectProvider from '../../CIMSSelect/common/ReportRecordSelect/ReportRecordSelectContext'
import CustomFieldEntityRecordSelectProvider from '../../CIMSSelect/common/CustomFieldEntityRecordSelect/CustomFieldEntityRecordSelectContext'
import CIMSSection from '../../CIMSSection'
import { HelpMessage } from '../../../utils/constants'

/**
 * components using this component must be wrapped in the {@link CustomFieldProvider}
 */
const CustomFieldEditor: React.FC<CustomFieldEditorProps> = ({
  hideBooleans,
  alreadyDisabled,
}) => {
  const { customFields, fetching, classType } = useProjectCustomFieldsContext()
  const [requiredFields, setRequiredFields] = useState<CustomField[]>([])
  const [optionalFields, setOptionalFields] = useState<CustomField[]>([])
  useEffect(() => {
    const fields = customFields
      .reduce<CustomField[]>((customFields, customField) => {
        const customFieldToClassType =
          customField.customFieldToClassTypes?.find(
            (cf2ct) => cf2ct.classTypeId === classType?.id
          )
        if (
          !customFieldToClassType ||
          (customField.deleted && !customFieldToClassType.alwaysVisible)
        )
          return customFields
        customFields.push({ ...customField, customFieldToClassType })
        return customFields
      }, [])
      .sort(alphabeticalSort('name'))
    setOptionalFields(
      fields.filter(
        ({ customFieldToClassType }) => !customFieldToClassType.required
      )
    )
    setRequiredFields(
      fields.filter(
        ({ customFieldToClassType }) => customFieldToClassType.required
      )
    )
  }, [classType, customFields])
  const filterMap = useCallback(
    (customFields: CustomField[]) =>
      customFields
        .filter(({ dataTypeId, metadata }) => {
          if (hideBooleans)
            return (
              dataTypeId !== CustomFieldDataType.Boolean.id &&
              metadata?.styleId !== CustomListItemStyle.Checkboxes.id
            )
          return true
        })
        .map((customField) => (
          <CustomFieldRow
            alreadyDisabled={alreadyDisabled}
            key={customField.id}
            customField={customField}
          />
        )),
    [alreadyDisabled, hideBooleans]
  )
  if (!filterMap(requiredFields).length && !filterMap(optionalFields).length)
    return null
  return (
    <CustomFieldEntityRecordSelectProvider>
      <ReportRecordSelectProvider>
        <CIMSSection
          title="Custom Fields"
          loading={fetching}
          help={classType && HelpMessage.CustomFieldEditor(classType)}
        >
          {filterMap(requiredFields)}
          {!!optionalFields.length && filterMap(optionalFields)}
        </CIMSSection>
      </ReportRecordSelectProvider>
    </CustomFieldEntityRecordSelectProvider>
  )
}

export default CustomFieldEditor
